import * as React from "react";
import { DateRange, DayPicker, OnSelectHandler } from "react-day-picker";
import { Resolution, TimePeriod } from "../types";
import { CalendarDateRangeIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { getAvailableResolutions, dateToDisplay, dateToDateOrDateRange } from "../utils";
import { endOfYesterday, startOfYesterday, subDays, subMonths, subWeeks, subYears, toDate } from "date-fns";

interface TimePeriodSelectorProps {
    loading: boolean;
    period: TimePeriod;
    setPeriod: React.Dispatch<React.SetStateAction<TimePeriod>>;
    resolution: Resolution;
    setResolution: React.Dispatch<React.SetStateAction<Resolution>>;
    date: Date;
    setDate: React.Dispatch<React.SetStateAction<Date>>;
}

const TimePeriodSelector: React.FC<TimePeriodSelectorProps> = ({ loading, period, setPeriod, resolution, setResolution, date, setDate }) => {
    const referenceDate = dateToDateOrDateRange(date, period);
    const [viewDate, setViewDate] = React.useState<Date>(date);
    const mode: 'single' | 'range' = period === TimePeriod.Day ? 'single' : 'range';

    const handleMonthChange = (date: Date) => {
        if (period === TimePeriod.Month) {
            if (date <= endOfYesterday()) {
                setDate(date);
            }
        }
        setViewDate(date);
    }

    const handleDatePickerOnSelectRange: OnSelectHandler<DateRange> = (dateRange) => {
        const { from: fromDate, to: toDate } = dateRange;
        const currentDateRange = referenceDate as { from: Date, to: Date };
        const currentFromDate = currentDateRange.from;
        const currentToDate = currentDateRange.to;
        if (fromDate && toDate) {
            if (fromDate < currentFromDate) {
                setDate(fromDate);
            } else if (toDate > currentToDate) {
                setDate(toDate);
            }
        }
    }

    const getSetToDate = (direction: 'back' | 'forward') => {
        let setToDate, setToViewDate;
        const diff = direction === 'back' ? 1 : -1;
        switch (period) {
            case TimePeriod.Day:
                setToDate = subDays(date, diff);
                setToViewDate = subDays(viewDate, diff);
                break;
            case TimePeriod.Week:
                setToDate = subWeeks(date, diff);
                setToViewDate = subWeeks(viewDate, diff);
                break;
            case TimePeriod.Month:
                setToDate = subMonths(date, diff);
                setToViewDate = subMonths(viewDate, diff);
                break;
            case TimePeriod.Year:
                setToDate = subYears(date, diff);
                setToViewDate = subYears(viewDate, diff);
                break;
        }
        return { setToDate, setToViewDate };
    }

    const handleBackForward = (direction: 'back' | 'forward') => {
        const { setToDate, setToViewDate } = getSetToDate(direction);
        if (setToDate <= endOfYesterday()) {
            setDate(setToDate);
            setViewDate(setToViewDate);
        }
    }

    return (
        <div className="flex gap-4 items-center">
            <div className="flex gap-2 text-sm text-white items-center">
                <span>Period</span>
                <select
                    className="px-1 py-1 rounded-md bg-pacificgreen-800 hover:bg-pacificgreen-700 focus:outline-none cursor-pointer"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value as TimePeriod)}
                    disabled={loading}
                >
                    {Object.values(TimePeriod).map((periodValue) => (
                        <option key={periodValue} value={periodValue}>{periodValue}</option>
                    ))}
                </select>
            </div>
            <div className="flex gap-2 text-sm text-white items-center">
                <span>Resolution</span>
                <select
                    className="px-1 py-1 rounded-md bg-pacificgreen-800 hover:bg-pacificgreen-700 focus:outline-none cursor-pointer"
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value as Resolution)}
                    disabled={loading}
                >
                    {getAvailableResolutions(period).map((resolutionValue) => (
                        <option key={resolutionValue} value={resolutionValue}>{resolutionValue}</option>
                    ))}
                </select>
            </div>
            <ChevronLeftIcon className={`button button-ghost size-4 ${getSetToDate('back').setToDate >= new Date(2024, 1, 1) ? "cursor-pointer" : "cursor-not-allowed"}`} onClick={() => handleBackForward('back')} />
            <div className="flex text-sm text-white items-center dropdown dropdown-bottom dropdown-end bg-pacificgreen-800 hover:bg-pacificgreen-700 rounded-md px-2 py-1">
                <div tabIndex={0} role="button" className="bg-transparent cursor-pointer flex items-center gap-2">
                    <CalendarDateRangeIcon className="size-4" />
                   {date
                        ? dateToDisplay(date, period)
                        : "Pick a date"
                    }
                </div>
                <div className="dropdown-content flex gap-2 [.dropdown:not(:focus-within)_&]:invisible">
                    {mode === 'single' ? (
                        <DayPicker
                            className="react-day-picker"
                            ISOWeek
                            showOutsideDays
                            navLayout="after"
                            captionLayout="dropdown"
                            mode="single"
                            required
                            month={viewDate}
                            onMonthChange={handleMonthChange}
                            selected={referenceDate as Date}
                            onSelect={setDate as OnSelectHandler<Date>}
                            disabled={loading
                                ? { dayOfWeek: [0, 1, 2, 3, 4, 5, 6] }
                                : {
                                    before: new Date(2024, 1, 1),
                                    after: toDate(startOfYesterday())
                                }
                            }
                        />
                    ) : (
                        <DayPicker
                            className="react-day-picker"
                            ISOWeek
                            showOutsideDays
                            navLayout="after"
                            captionLayout="dropdown"
                            mode="range"
                            required
                            month={viewDate}
                            onMonthChange={handleMonthChange}
                            selected={referenceDate as DateRange}
                            onSelect={handleDatePickerOnSelectRange}
                            disabled={loading
                                ? { dayOfWeek: [0, 1, 2, 3, 4, 5, 6] }
                                : {
                                    before: new Date(2024, 1, 1),
                                    after: toDate(startOfYesterday())
                                }
                            }
                        />
                    )}
                </div>
            </div>
            <ChevronRightIcon className={`button button-ghost size-4 ${getSetToDate('forward').setToDate <= endOfYesterday() ? "cursor-pointer" : "cursor-not-allowed"}`} onClick={() => handleBackForward('forward')} />
        </div>
    );
};

export default TimePeriodSelector;